import * as React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { api, ErroHttp } from '@/lib/api';
import { useCracha } from '@/hooks/useCracha';
import { imprimirEtiquetas } from '@/lib/imprimirEtiquetas';
import { DanfeSimplificada, type VolumeDanfe } from '@/componentes/expedicao/DanfeSimplificada';

/**
 * Impressão automática da etiqueta de expedição — o lugar do
 * ERPM_ETIQUETAS.exe, rodando DENTRO do WMS.
 *
 * Mora no AppShell, e não numa página, porque o operador do checkout não
 * fica parado numa tela: confere, fatura, consulta estoque. Enquanto houver
 * uma aba do WMS aberta, em qualquer tela, fica uma requisição esperando
 * etiqueta do crachá do login; a API responde no instante em que ela aparece
 * (já marcada como impressa) e ela sai na impressora — sem diálogo quando o
 * Chrome do checkout roda com `--kiosk-printing`.
 *
 * O crachá é o EM USO na sessão, a mesma regra do faturamento: o trocado na
 * tela, ou o do cadastro. Trocou o crachá no Faturamento, a impressão passa a
 * seguir o novo na hora. O servidor garante um computador por crachá:
 * etiqueta do 40 não sai na sessão do 30, e se o 30 já imprime em outro
 * checkout esta aba para e avisa, em vez de dividir as etiquetas. A pessoa
 * não configura nada nem percebe que isto existe. Só o crachá que
 * fatura tem linha em `ETIQUETAS`, então para os outros a pergunta volta
 * sempre vazia. Um computador que não deve imprimir (sem Zebra) pode ser
 * desligado na tela de impressão — vale só para ele.
 *
 * Com várias abas abertas, a trava do navegador (`navigator.locks`) deixa uma
 * só perguntar — as outras assumem se ela fechar.
 */

export interface EtiquetaExpedicao {
  registro: number;
  televenda: number;
  pedidaEm: string | null;
  volumes: VolumeDanfe[];
}

export interface Impressa extends EtiquetaExpedicao {
  impressaEm: string;
  manual: boolean;
}

export type EstadoImpressao =
  | { tipo: 'desligada' }
  /** A chave geral do Admin está desligada: quem imprime é o .exe. */
  | { tipo: 'desativada' }
  /** Outra aba do WMS neste computador é quem está imprimindo. */
  | { tipo: 'outra_aba' }
  | { tipo: 'ok'; ultimaVolta: Date }
  | { tipo: 'imprimindo' }
  /** O crachá já imprime em outro computador; esta aba parou e espera ordem. */
  | { tipo: 'outro_computador'; mensagem: string }
  | { tipo: 'erro'; codigo: 'sessao' | 'bloqueado' | 'rede'; mensagem: string };

interface ValorImpressao {
  /** O crachá que está imprimindo aqui — `null` sem crachá ou desligado. */
  cracha: string | null;
  /** O crachá da sessão, ligado ou não. */
  crachaDaSessao: string | null;
  desligadoAqui: boolean;
  definirDesligadoAqui: (v: boolean) => void;
  estado: EstadoImpressao;
  historico: Impressa[];
  registrar: (e: EtiquetaExpedicao[], manual: boolean) => void;
  /** "Imprimir aqui": toma o crachá do outro computador. */
  assumir: () => void;
}

const CHAVE_DESLIGADO = 'wms.etiquetasExpedicao.desligado';
const CHAVE_HISTORICO = 'wms.etiquetasExpedicao.historico';
const HISTORICO_MAX = 30;

function lerLocal<T>(chave: string, padrao: T): T {
  try {
    const bruto = localStorage.getItem(chave);
    return bruto ? (JSON.parse(bruto) as T) : padrao;
  } catch {
    return padrao;
  }
}

function gravarLocal(chave: string, valor: unknown): void {
  try {
    if (valor === null) localStorage.removeItem(chave);
    else localStorage.setItem(chave, JSON.stringify(valor));
  } catch {
    /* storage bloqueado: vale enquanto a aba estiver aberta */
  }
}

const esperar = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));

/** Renderiza as etiquetas fora da tela e imprime num documento de 100 × 150 mm. */
export function imprimirVolumes(volumes: VolumeDanfe[], teste = false): Promise<void> {
  return new Promise((resolve) => {
    const host = document.createElement('div');
    const raiz = createRoot(host);
    flushSync(() =>
      raiz.render(
        <>
          {volumes.map((v, i) => (
            <DanfeSimplificada key={i} v={v} teste={teste} />
          ))}
        </>,
      ),
    );
    imprimirEtiquetas(host, {
      larguraMm: 100,
      alturaMm: 150,
      titulo: 'Etiquetas de expedicao',
      aoTerminar: () => {
        raiz.unmount();
        resolve();
      },
    });
  });
}

const Contexto = React.createContext<ValorImpressao | null>(null);

export function ProvedorImpressaoExpedicao({ children }: { children: React.ReactNode }): JSX.Element {
  const crachaDaSessao = useCracha().cracha;
  const [desligadoAqui, definirDesligadoEstado] = React.useState<boolean>(() =>
    lerLocal<boolean>(CHAVE_DESLIGADO, false),
  );
  const cracha = desligadoAqui ? null : crachaDaSessao;
  const [historico, definirHistorico] = React.useState<Impressa[]>(() =>
    lerLocal<Impressa[]>(CHAVE_HISTORICO, []),
  );
  const [estado, definirEstado] = React.useState<EstadoImpressao>({ tipo: 'desligada' });
  // Muda para recomeçar a volta tomando o crachá ("Imprimir aqui").
  const [tomada, definirTomada] = React.useState(0);
  const assumir = React.useCallback(() => definirTomada((n) => n + 1), []);

  const definirDesligadoAqui = React.useCallback((v: boolean) => {
    gravarLocal(CHAVE_DESLIGADO, v ? true : null);
    definirDesligadoEstado(v);
  }, []);

  const registrar = React.useCallback((novas: EtiquetaExpedicao[], manual: boolean) => {
    const agora = new Date().toISOString();
    definirHistorico((h) => {
      const prox = [...novas.map((e) => ({ ...e, impressaEm: agora, manual })), ...h].slice(0, HISTORICO_MAX);
      gravarLocal(CHAVE_HISTORICO, prox);
      return prox;
    });
  }, []);

  // Outra aba ligou ou desligou: esta acompanha.
  React.useEffect(() => {
    const aoMudar = (e: StorageEvent): void => {
      if (e.key === CHAVE_DESLIGADO) definirDesligadoEstado(lerLocal<boolean>(CHAVE_DESLIGADO, false));
      if (e.key === CHAVE_HISTORICO) definirHistorico(lerLocal<Impressa[]>(CHAVE_HISTORICO, []));
    };
    window.addEventListener('storage', aoMudar);
    return () => window.removeEventListener('storage', aoMudar);
  }, []);

  // A volta: pergunta, imprime, espera.
  React.useEffect(() => {
    if (!cracha) {
      definirEstado({ tipo: 'desligada' });
      return;
    }
    let parar = false;
    let tomar = tomada > 0;
    const controle = new AbortController();

    const rodar = async (): Promise<void> => {
      while (!parar) {
        // A requisição ESPERA no servidor até haver etiqueta (ou 20 s): por
        // isso, com resposta boa, a próxima sai na hora — sem intervalo.
        let espera = 0;
        try {
          // Cancelada na troca de crachá: o servidor devolve à fila o que tiver
          // marcado, e a volta do crachá novo não espera os 20 s desta.
          const r = await api.post<{ etiquetas: EtiquetaExpedicao[]; desativada?: boolean }>(
            '/api/etiquetas-expedicao/pegar',
            { cracha, assumir: tomar },
            undefined,
            controle.signal,
          );
          tomar = false;
          // Imprime mesmo se a volta acabou de ser encerrada (troca de crachá):
          // estas já foram marcadas para este computador.
          if (r.etiquetas.length > 0) {
            definirEstado({ tipo: 'imprimindo' });
            await imprimirVolumes(r.etiquetas.flatMap((e) => e.volumes));
            registrar(r.etiquetas, false);
          }
          if (parar) break;
          if (r.desativada) {
            // Desligada no Admin: quieta, e confere de novo daqui a 1 min.
            definirEstado({ tipo: 'desativada' });
            espera = 60_000;
          } else {
            definirEstado({ tipo: 'ok', ultimaVolta: new Date() });
          }
        } catch (err) {
          if (parar) break;
          if (err instanceof ErroHttp && err.status === 409) {
            // Não toma à força: viraria cabo de guerra entre dois checkouts.
            // Mas volta a perguntar SEM forçar — o servidor solta o crachá
            // quando o dono some (45 s). Parar de vez deixava a fila sem
            // ninguém: trocar do Chrome comum para o atalho WMS com os dois
            // abertos um instante, fechar o antigo, e nada mais saía (26/10).
            definirEstado({ tipo: 'outro_computador', mensagem: err.message });
            espera = 30_000;
          } else {
            if (err instanceof ErroHttp && err.naoAutenticado) {
              definirEstado({
                tipo: 'erro',
                codigo: 'sessao',
                mensagem: 'A sessão expirou. As etiquetas NÃO estão saindo até alguém entrar de novo.',
              });
            } else if (err instanceof ErroHttp && err.status === 423) {
              definirEstado({ tipo: 'erro', codigo: 'bloqueado', mensagem: err.message });
            } else {
              definirEstado({
                tipo: 'erro',
                codigo: 'rede',
                mensagem: err instanceof Error ? err.message : 'Sem resposta do servidor.',
              });
            }
            espera = 5_000;
          }
        }
        if (espera > 0) await esperar(espera);
      }
    };

    if ('locks' in navigator) {
      definirEstado({ tipo: 'outra_aba' });
      navigator.locks
        .request('wms-etiquetas-expedicao', { signal: controle.signal }, () => rodar())
        .catch(() => undefined);
    } else {
      void rodar();
    }
    return () => {
      parar = true;
      controle.abort();
    };
  }, [cracha, registrar, tomada]);

  const valor = React.useMemo(
    () => ({ cracha, crachaDaSessao, desligadoAqui, definirDesligadoAqui, estado, historico, registrar, assumir }),
    [cracha, crachaDaSessao, desligadoAqui, definirDesligadoAqui, estado, historico, registrar, assumir],
  );
  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>;
}

export function useImpressaoExpedicao(): ValorImpressao {
  const valor = React.useContext(Contexto);
  if (!valor) throw new Error('useImpressaoExpedicao fora do ProvedorImpressaoExpedicao');
  return valor;
}
